"use client";

import { useState } from "react";
import Link from "next/link";

type Status = "idle" | "submitting" | "sent" | "error";
type RequestType = "delete" | "access" | "correction" | "optout";

export default function PrivacyRequestPage() {
  const [requestType, setRequestType] = useState<RequestType>("delete");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [details, setDetails] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setStatus("submitting");
    setError("");
    try {
      const res = await fetch("/api/privacy/request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestType, email, name, details }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.error ?? "Submission failed.");
        setStatus("error");
        return;
      }
      setStatus("sent");
    } catch {
      setError("Network error. Try again or email us directly.");
      setStatus("error");
    }
  }

  if (status === "sent") {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 text-center">
        <div className="w-14 h-14 bg-green-100 flex items-center justify-center mx-auto mb-6">
          <svg className="w-7 h-7 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="font-headline text-4xl text-black uppercase mb-3">
          Request Received
        </h1>
        <p className="text-gray-500 max-w-md mx-auto mb-8">
          We&rsquo;ll verify the request and respond within 45 days, as required by
          applicable privacy law. You&rsquo;ll hear from{" "}
          <span className="font-semibold text-black">online@areaadvertising.com</span>.
        </p>
        <Link href="/privacy" className="btn-outline-dark">
          ← Back to Privacy Policy
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <section className="bg-black text-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <p className="text-brand-orange uppercase tracking-[0.3em] text-xs font-bold mb-4">
            Privacy Rights
          </p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4">
            Data Request
          </h1>
          <p className="text-white/60 max-w-xl">
            Submit a request to access, correct, or delete your personal information.
            We respond to verified requests within 45 days.
          </p>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <form onSubmit={submit} className="space-y-6">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-3">
              Request Type *
            </label>
            <div className="space-y-2">
              {(
                [
                  { v: "delete", l: "Delete my personal information" },
                  { v: "access", l: "Tell me what data you have about me" },
                  { v: "correction", l: "Correct inaccurate data you have about me" },
                  { v: "optout", l: "Opt out of marketing email and ad matching" },
                ] as { v: RequestType; l: string }[]
              ).map((opt) => (
                <label
                  key={opt.v}
                  className={`block px-4 py-3 border-2 cursor-pointer transition-colors ${
                    requestType === opt.v
                      ? "border-brand-orange bg-orange-50"
                      : "border-gray-200 hover:border-gray-400"
                  }`}
                >
                  <input
                    type="radio"
                    name="requestType"
                    value={opt.v}
                    checked={requestType === opt.v}
                    onChange={() => setRequestType(opt.v)}
                    className="mr-3 accent-brand-orange"
                  />
                  <span className="text-sm font-medium text-gray-900">{opt.l}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-2">
              Email Associated With Your Data *
            </label>
            <input
              id="email"
              required
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 focus:border-brand-red focus:bg-white outline-none transition-all text-gray-900"
              placeholder="you@example.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              We&rsquo;ll send a confirmation here to verify the request belongs to you.
            </p>
          </div>

          <div>
            <label htmlFor="name" className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-2">
              Name (optional)
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 focus:border-brand-red focus:bg-white outline-none transition-all text-gray-900"
              placeholder="Jane Doe"
            />
          </div>

          <div>
            <label htmlFor="details" className="block text-[11px] font-semibold uppercase tracking-[0.2em] text-gray-700 mb-2">
              Additional Details (optional)
            </label>
            <textarea
              id="details"
              rows={4}
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              maxLength={2000}
              className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 focus:border-brand-red focus:bg-white outline-none transition-all text-gray-900 resize-y"
              placeholder="e.g. previous email addresses, phone numbers, or order context that might help us find your records."
            />
          </div>

          {error && (
            <p className="text-red-600 text-sm">{error}</p>
          )}

          <button
            type="submit"
            disabled={status === "submitting" || !email}
            className="btn-outline-dark w-full sm:w-auto disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {status === "submitting" ? "Sending…" : "Submit Request →"}
          </button>

          <p className="text-xs text-gray-400 mt-6">
            Prefer email? Send your request to{" "}
            <a href="mailto:online@areaadvertising.com" className="text-brand-orange hover:underline">
              online@areaadvertising.com
            </a>{" "}
            with the subject &ldquo;Data Request.&rdquo;
          </p>
        </form>
      </div>
    </div>
  );
}
